// src/components/general-task/GeneralTaskDashboard.tsx
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/Card';
import { Skeleton } from '@/components/ui/skeleton';
import { GeneralTaskDTO, GeneralTaskApi } from './GeneralTaskApi';
import { cn } from '@/lib/utils';
import {
  FaTasks,
  FaCheckCircle,
  FaClock,
  FaMoneyBillWave,
  FaList,
  FaArrowLeft,
  FaPlus,
  FaTable,
} from 'react-icons/fa';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLock } from '@fortawesome/free-solid-svg-icons';

import { MODULES } from '../../constants/globalConstants';
import {
  readUserFromLocalStorage,
  hasPermission,            // hasPermission(user, moduleName, ['view','report'], 'any')
  getModulePermissionRow,   // returns the module permission row (to read visibleInDashboard, etc.)
} from '../../utils/permissions';
import type { UserDTO } from '../auth/AuthApi';

interface KPI {
  label: string;
  icon: React.ReactNode;
  value: number | string;
  color?: string;
}

export const GeneralTaskDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [tasks, setTasks] = useState<GeneralTaskDTO[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  // ---- Role gating ----
  const user = readUserFromLocalStorage<UserDTO>();
  const permRow = useMemo(
    () => getModulePermissionRow(user || null, MODULES.GENERAL_TASKS) || null,
    [user]
  );

  // Can see dashboard if (view OR report) AND (visibleInDashboard !== false)
  const canSeeDashboard = useMemo(() => {
    const viewOrReport = hasPermission(user || null, MODULES.GENERAL_TASKS, ['view', 'report'], 'any');
    const visibleFlag =
      permRow && typeof permRow.visibleInDashboard === 'boolean'
        ? !!permRow.visibleInDashboard
        : true; // default allow if not configured
    return viewOrReport && visibleFlag;
  }, [permRow, user]);

  const canList = useMemo(
    () => hasPermission(user || null, MODULES.GENERAL_TASKS, ['view', 'report'], 'any'),
    [user]
  );
  const canCreate = useMemo(
    () => hasPermission(user || null, MODULES.GENERAL_TASKS, 'create', 'any'),
    [user]
  );

  // ---- Data load (only if authorized) ----
  useEffect(() => {
    if (!canSeeDashboard) {
      setLoading(false);
      setTasks([]);
      return;
    }
    const fetchTasks = async () => {
      setLoading(true);
      try {
        const response = await GeneralTaskApi.fetchAllGeneralTasks();
        setTasks(response.data?.content || response.data || []);
      } catch (error) {
        console.error('Failed to fetch general tasks:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchTasks();
  }, [canSeeDashboard]);

  const getKPIs = (): KPI[] => {
    const total = tasks.length;
    const completed = tasks.filter(t => (t.status || '').toLowerCase() === 'completed').length;
    const pending = tasks.filter(t => (t.status || '').toLowerCase() !== 'completed').length;
    const billable = tasks.filter(t => !!t.isBillable).length;
    const totalHours = tasks.reduce((acc, t) => acc + (t.actualHours || 0), 0);

    return [
      { label: 'Total Tasks',     icon: <FaTasks className="text-blue-500" />,        value: total },
      { label: 'Completed',       icon: <FaCheckCircle className="text-green-500" />, value: completed },
      { label: 'Pending',         icon: <FaClock className="text-yellow-500" />,      value: pending },
      { label: 'Billable Tasks',  icon: <FaMoneyBillWave className="text-purple-500" />, value: billable },
      { label: 'Total Hours',     icon: <FaList className="text-pink-500" />,         value: totalHours.toFixed(2) },
    ];
  };

  // Unauthorized screen
  if (!canSeeDashboard) {
    return (
      <div className="p-6">
        <div className="max-w-2xl mx-auto bg-white border rounded-xl p-6 text-center">
          <div className="flex items-center justify-center mb-3">
            <div className="w-12 h-12 rounded-full border flex items-center justify-center text-slate-600">
              <FontAwesomeIcon icon={faLock} />
            </div>
          </div>
          <h2 className="text-xl font-semibold mb-1">Access Restricted</h2>
          <p className="text-slate-600">
            You don’t have permission to view the General Task dashboard. If you believe this is an
            error, please contact your administrator.
          </p>
        </div>
      </div>
    );
  }

  const kpis = getKPIs();

  return (
    <div className="p-6 space-y-6">
      {/* Navigation Buttons */}
      <div className="flex flex-wrap gap-3 items-center justify-start">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 bg-gray-200 hover:bg-gray-300 text-gray-700 px-4 py-2 rounded-xl text-sm font-medium shadow"
        >
          <FaArrowLeft /> Back
        </button>

        {canList && (
          <button
            onClick={() => navigate('/general-tasks/list')}
            className="flex items-center gap-2 bg-blue-100 hover:bg-blue-200 text-blue-700 px-4 py-2 rounded-xl text-sm font-medium shadow"
          >
            <FaTable /> General Task List
          </button>
        )}

        {canCreate && (
          <button
            onClick={() => navigate('/general-tasks/create')}
            className="flex items-center gap-2 bg-green-100 hover:bg-green-200 text-green-700 px-4 py-2 rounded-xl text-sm font-medium shadow"
          >
            <FaPlus /> Create General Task
          </button>
        )}
      </div>

      {/* KPI Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
        {loading
          ? Array.from({ length: 5 }).map((_, idx) => (
              <Skeleton key={idx} className="h-[120px] rounded-2xl" />
            ))
          : kpis.map((kpi, index) => (
              <Card key={index} className="shadow-lg rounded-2xl">
                <CardContent className="p-4 flex items-center space-x-4">
                  <div className={cn('text-3xl')}>{kpi.icon}</div>
                  <div>
                    <div className="text-xl font-bold">{kpi.value}</div>
                    <div className="text-sm text-muted-foreground">{kpi.label}</div>
                  </div>
                </CardContent>
              </Card>
            ))}
      </div>
    </div>
  );
};